import type { Channel } from 'phoenix'
import { Socket } from 'phoenix'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { z } from 'zod'
import { isAuthenticatedPlayer, type Balance } from 'src/types/domain/Player'
import type { InventoryItem } from 'src/features/inventory'
import { InventoryResponseDtoSchema, fromInventoryItemsDTO } from 'src/features/inventory'
import type { DailyStreakDTO } from 'src/features/dailyCheckIn'
import { DailyStreakSchema } from 'src/features/dailyCheckIn/types/schema'
import { fromDailyStreakDTO } from 'src/features/dailyCheckIn/mappers'
import { useDailyCheckInStore } from 'src/features/dailyCheckIn'
import { usePlayerStore } from './player'
import { isDevtoolsEnabled } from './utils/isDevtoolsEnabled'
import { stopIfTokenExpired } from './utils/stopIfTokenExpired'
import { extractErrorInfo } from './utils/extractErrorInfo'
import { useErrorStore } from './error'
import { joinChannelWithTimeout, joinChannelWithResponse } from './utils/joinChannelWithTimeout'
import { leaveChannelWithTimeout } from './utils/leaveChannelWithTimeout'
import {
  beginSocialJoin,
  createBoundChannel,
  endSocialJoin,
  rememberSocialBinder,
  shouldReleaseOnClose,
  socialTopicFor,
  takeSocialBinder,
  type SocialBinder
} from './utils/socialChannel'

interface SocketState {
  socket?: Socket
  playerChannel?: Channel
  gameChannel?: Channel
  raceChannel?: Channel
  socialChannel?: Channel
  joinedGameChannelGameId?: string
  isJoiningRaceChannel?: boolean
  /**
   * Bumped on EVERY successful player-channel join, including phoenix-js's own
   * silent rejoins (which reuse the same Channel object, so watching the
   * channel identity misses them entirely).
   *
   * Consumers use it to re-pull player state the join reply does NOT replay.
   * The reply carries games/streaks/inventory but no bet states, so any
   * at-most-once push that landed inside the reconnect window — `bet:cashed-out`
   * above all — is gone for good; only an explicit `bets:get` recovers it.
   */
  playerChannelJoinEpoch: number
}

interface AllowedGame {
  id: string
}

interface PlayerStatsResponse {
  playerId: string
  level: number
  xp: number // In cents (same as totalWageredUSD)
  nextLevelXP?: number | null
  multiplierBonus: number
  totalBets: number
  totalWins: number
  totalLosses: number
  winRate: number
  // Currency-converted values (optional - may be missing if session not found)
  totalWagered?: number // In player's currency minor units
  totalWon?: number // Total amount won in player's currency minor units
  biggestWin?: number // In player's currency minor units
  currency?: string // Player's session currency code
}

interface JoinPlayerChannelResponse {
  player: {
    // `amount` may be absent when the server defers balance for a non-blocking
    // launch (currency is always known immediately); it's then filled by the
    // post-join balance fetch.
    balance: { amount?: number; currency: string }
    country?: string
    displayName: string
    id: string
    language?: string
    platform: 'MOBILE' | 'DESKTOP'
    sessionId: string
    username: string
  }
  games: AllowedGame[]
  gameConfig: {
    lobbyDisabled: boolean
    externalDepositUrl?: string
    externalLobbyUrl?: string
  }
  inventory: InventoryItem[]
  streaks?: DailyStreakDTO[]
  playerStats?: PlayerStatsResponse | null
}

const BalanceSchema = z.object({
  amount: z.number(),
  currency: z.string()
})

// Join-reply balance: `amount` is optional because a non-blocking launch defers
// it to the post-join balance fetch. Currency is always present. A reply WITH
// amount (current server / demo) still works unchanged.
const JoinBalanceSchema = z.object({
  amount: z.number().optional(),
  currency: z.string()
})

const PlayerStatsSchema = z.object({
  playerId: z.string(),
  level: z.number(),
  xp: z.number(),
  nextLevelXP: z.number().optional().nullable(),
  multiplierBonus: z.number(),
  totalBets: z.number(),
  totalWins: z.number(),
  totalLosses: z.number(),
  winRate: z.number(),
  // Currency-converted values (optional - may be missing if session not found)
  totalWagered: z.number().optional(),
  totalWon: z.number().optional(),
  biggestWin: z.number().optional(),
  currency: z.string().optional()
})

const JoinPlayerChannelResponseSchema = z.object({
  player: z.object({
    balance: JoinBalanceSchema,
    country: z.string().optional().nullable(),
    displayName: z.string(),
    id: z.string(),
    language: z.string().optional().nullable(),
    platform: z.enum(['MOBILE', 'DESKTOP']),
    sessionId: z.string(),
    username: z.string()
  }),
  games: z.array(
    z.object({
      id: z.string()
    })
  ),
  gameWidgetConfig: z.object({
    // Operators sometimes send malformed deposit/lobby URLs (e.g. "3773"). These are
    // cosmetic (deposit / back-to-lobby buttons) and must never fail the join and lock
    // the player out with "we were not able to authenticate you" — so do not enforce url().
    externalDepositUrl: z.string().optional().nullable(),
    externalLobbyUrl: z.string().optional().nullable(),
    lobbyDisabled: z.boolean().optional().nullable()
  }),
  inventory: InventoryResponseDtoSchema.shape.items,
  streaks: z.array(DailyStreakSchema).optional(),
  playerStats: PlayerStatsSchema.optional().nullable()
})

export interface SocketActions {
  createSocket: (scheme: string, url: string, token: string) => void
  joinPlayerChannel: (
    playerId: string,
    onJoin?: (response: JoinPlayerChannelResponse) => void
  ) => Promise<void>
  leavePlayerChannel: () => Promise<void>
  joinGameChannel: (gameId: string) => Promise<void>
  leaveGameChannel: () => Promise<void>
  joinRaceChannel: () => Promise<void>
  leaveRaceChannel: () => Promise<void>
  /**
   * `bind` registers the social listeners on the channel BEFORE it is joined —
   * the server pushes `round:snapshot` immediately after the join reply and
   * phoenix-js drops unbound events. It is remembered so `rejoinAllChannels`,
   * which has no access to the React-owned handlers, can re-bind on reconnect.
   */
  joinSocialChannel: (gameId: string, bind?: SocialBinder) => Promise<void>
  leaveSocialChannel: () => Promise<void>
  rejoinAllChannels: () => void
}

type SocketStore = SocketState & {
  actions: SocketActions
}

const SOCKET_TIMEOUT = 7_000

export const useSocketStore = create<SocketStore>()(
  devtools(
    immer((set) => ({
      socket: undefined,
      playerChannel: undefined,
      gameChannel: undefined,
      raceChannel: undefined,
      socialChannel: undefined,
      joinedGameChannelGameId: undefined,
      isJoiningRaceChannel: false,
      playerChannelJoinEpoch: 0,
      actions: {
        createSocket: (scheme: string, url: string, token: string) => {
          set((state) => {
            const socket = new Socket(`${scheme}://${url}/socket`, {
              params: { token },
              timeout: SOCKET_TIMEOUT,
              heartbeatIntervalMs: 20_000,
              debug: false,
              reconnectAfterMs: (tries) => {
                // It starts with a base of 500ms and increases by 250ms for each attempt,
                // capping at a maximum of 3000ms (3 seconds).
                return Math.min(500 + tries * 250, 3000)
              }
            })

            socket.onOpen(() => {
              // console.log('[SOCKET] Connected, triggering rejoin')
              useSocketStore.getState().actions.rejoinAllChannels()
            })

            // Stop re-dialing a dead token forever: once `token` expires, every
            // reconnect is rejected (token_expired) and Phoenix would otherwise
            // loop every ~3s, churning the socket-server. A still-valid token is
            // left alone so transient network reconnects keep working.
            socket.onError(() => {
              stopIfTokenExpired(socket, token)
            })

            socket.connect()
            state.socket = socket
          })
        },
        joinSocialChannel: async (gameId: string, bind?: SocialBinder) => {
          const state = useSocketStore.getState()

          // Remember the React-owned binder so the reconnect path
          // (rejoinAllChannels), which cannot see those handlers, re-binds too.
          if (bind) rememberSocialBinder(bind)

          // Single-flight. Releasing the channel on close re-fires the join
          // effect, and socket.onOpen → rejoinAllChannels fires its own join, so
          // the two recovery paths can overlap and issue two phx_join for one
          // topic. The server closes the duplicate, which releases again — a
          // rejoin loop assembled from two individually correct behaviours.
          if (!beginSocialJoin(gameId)) return

          if (!state.socket) {
            console.error('[SOCIAL CHANNEL] Socket not connected')
            return
          }

          // console.log(`[SOCIAL CHANNEL] Starting join for game ${gameId}`)

          // Leave and clean up previous channel
          if (state.socialChannel) {
            // console.log('[SOCIAL CHANNEL] Cleaning up previous channel:', state.socialChannel.topic)
            await leaveChannelWithTimeout(state.socialChannel)
            set(
              (state) => {
                state.socialChannel = undefined
              },
              false,
              'joinSocialChannel - cleanup'
            )
          }

          // Create the channel WITH its listeners already attached. The server
          // pushes `round:snapshot` straight after the join reply and phoenix-js
          // drops unbound events, so binding after the join resolves loses a
          // mid-round joiner the whole pre-join history of the round.
          const channel = createBoundChannel(
            state.socket,
            socialTopicFor(gameId),
            takeSocialBinder()
          )

          // phx_close is TERMINAL in phoenix-js: onClose resets the rejoin timer
          // and removes the channel from the socket, so unlike an ERRORED
          // channel (which self-rejoins) a CLOSED one never comes back. With no
          // hook here the client just went quiet — socket alive, feed frozen —
          // until an unrelated re-render happened to re-arm the join effect.
          // Releasing the store entry flips the join guard on the next commit,
          // independent of how often this player's screen re-renders.
          channel.onClose(() => {
            if (!shouldReleaseOnClose(useSocketStore.getState().socialChannel, channel)) return

            set(
              (state) => {
                state.socialChannel = undefined
              },
              false,
              'joinSocialChannel - closed'
            )
          })

          try {
            await joinChannelWithTimeout(channel, SOCKET_TIMEOUT)
            // console.log('[SOCIAL CHANNEL] Successfully joined, channel state:', channel.state)

            set(
              (state) => {
                state.socialChannel = channel
              },
              false,
              'joinSocialChannel - success'
            )
          } catch (error) {
            console.error('[SOCIAL CHANNEL] Join failed:', error)

            // LEAVE the failed channel — matching joinRaceChannel and
            // joinPlayerChannel, which both do this. phoenix-js keeps an
            // errored channel in socket.channels and retries phx_join on its
            // own timer forever. That orphan used to be harmless because
            // listeners were attached to the STORE's channel after the join
            // resolved; now they are bound at construction, so an abandoned
            // channel is fully live. Left alone it eventually rejoins, the
            // server answers with `round:snapshot` for the game it belongs to,
            // and after a game switch that lands another game's bets in this
            // game's Previous tab.
            await leaveChannelWithTimeout(channel)

            set(
              (state) => {
                state.socialChannel = undefined
              },
              false,
              'joinSocialChannel - error'
            )
          } finally {
            // Cleared on BOTH paths — a failed join must not park every later
            // attempt behind it, which would turn one bad join into a
            // permanently silent feed. KEYED, so a late-settling join for the
            // game we just left cannot release the lock a newer game now holds.
            endSocialJoin(gameId)
          }
        },
        leaveSocialChannel: async () => {
          const { socialChannel } = useSocketStore.getState()

          if (!socialChannel) {
            // console.log('[SOCIAL CHANNEL] No channel to leave')
            return
          }

          // console.log('[SOCIAL CHANNEL] Leaving channel:', socialChannel.topic, 'state:', socialChannel.state)
          await leaveChannelWithTimeout(socialChannel)
          // console.log('[SOCIAL CHANNEL] Successfully left channel')

          set(
            (state) => {
              state.socialChannel = undefined
            },
            false,
            'leaveSocialChannel'
          )
        },
        joinPlayerChannel: async (
          playerId: string,
          onJoin?: (response: JoinPlayerChannelResponse) => void
        ) => {
          const state = useSocketStore.getState()
          const errorState = useErrorStore.getState()

          if (!state.socket) {
            console.error('[PLAYER CHANNEL] Socket not connected')
            return
          }

          // Don't attempt to join if we already have a fatal error (prevents infinite retries)
          if (errorState.fatalError?.type === 'PLAYER_CHANNEL_FAILED') {
            console.warn('[PLAYER CHANNEL] Previous fatal error exists, not attempting to join')
            return
          }

          // Skip if already joined (check topic to ensure it's for the right player)
          if (
            state.playerChannel?.state === 'joined' &&
            state.playerChannel.topic === `realtime:private:player-${playerId}`
          ) {
            return
          }

          // Skip if currently joining the same channel
          if (
            state.playerChannel?.state === 'joining' &&
            state.playerChannel.topic === `realtime:private:player-${playerId}`
          ) {
            return
          }

          // Leave and clean up previous channel
          if (state.playerChannel) {
            await leaveChannelWithTimeout(state.playerChannel)
            set(
              (state) => {
                state.playerChannel = undefined
              },
              false,
              'joinPlayerChannel - cleanup'
            )
          }

          // Create new channel
          const channel = state.socket.channel(`realtime:private:player-${playerId}`)

          try {
            // Join the channel and wait for response using consistent timeout pattern.
            // The third argument catches the rejoins phoenix-js performs on its
            // own timer: those keep this Channel object (so the store never
            // changes) yet still lose every push that arrived while the channel
            // was errored. Guarded on identity so an abandoned channel — one we
            // replaced and left — can't bump the epoch from the grave.
            const response = await joinChannelWithResponse(channel, SOCKET_TIMEOUT, () => {
              if (useSocketStore.getState().playerChannel !== channel) return
              set(
                (state) => {
                  state.playerChannelJoinEpoch += 1
                },
                false,
                'joinPlayerChannel - rejoined'
              )
            })

            const parsedResponse = JoinPlayerChannelResponseSchema.safeParse(response)

            if (!parsedResponse.success) {
              usePlayerStore.getState().actions.notAuthenticated()

              console.error(
                '[PLAYER CHANNEL] Invalid response from channel join:',
                parsedResponse.error
              )

              // Clean up and clear from state
              await leaveChannelWithTimeout(channel)
              set(
                (state) => {
                  state.playerChannel = undefined
                },
                false,
                'joinPlayerChannel - invalid response'
              )
              return
            }

            const transformedResponse: JoinPlayerChannelResponse = {
              player: {
                id: parsedResponse.data.player.id,
                displayName: parsedResponse.data.player.displayName,
                balance: parsedResponse.data.player.balance,
                country: parsedResponse.data.player.country ?? undefined,
                language: parsedResponse.data.player.language ?? undefined,
                platform: parsedResponse.data.player.platform,
                sessionId: parsedResponse.data.player.sessionId,
                username: parsedResponse.data.player.username
              },
              games: parsedResponse.data.games,
              gameConfig: {
                externalDepositUrl:
                  parsedResponse.data.gameWidgetConfig.externalDepositUrl ?? undefined,
                externalLobbyUrl:
                  parsedResponse.data.gameWidgetConfig.externalLobbyUrl ?? undefined,
                lobbyDisabled: parsedResponse.data.gameWidgetConfig.lobbyDisabled ?? false
              },
              inventory: fromInventoryItemsDTO(parsedResponse.data.inventory),
              streaks: parsedResponse.data.streaks,
              playerStats: parsedResponse.data.playerStats ?? undefined
            }

            // Process streaks if present
            if (parsedResponse.data.streaks && parsedResponse.data.streaks.length > 0) {
              // Find daily streak
              const dailyStreak = parsedResponse.data.streaks.find(
                (streak) => streak.type === 'daily_streak'
              )
              if (dailyStreak) {
                const mappedStreak = fromDailyStreakDTO(dailyStreak)
                useDailyCheckInStore.getState().actions.setDailyCheckInData(mappedStreak)
              }
            }

            // Store channel in state only after successful join and response
            // validation. Channel + epoch move in ONE set so a consumer keyed on
            // both re-runs exactly once per join, not twice.
            set(
              (state) => {
                state.playerChannel = channel
                state.playerChannelJoinEpoch += 1
              },
              false,
              'joinPlayerChannel - success'
            )

            onJoin?.(transformedResponse)

            // console.log('[PLAYER CHANNEL] Successfully joined, channel state:', channel.state)
          } catch (error) {
            console.error('[PLAYER CHANNEL] Join failed:', error)

            const { reason, details } = extractErrorInfo(error)

            // Set fatal error
            errorState.actions.setFatalError({
              type: 'PLAYER_CHANNEL_FAILED',
              reason,
              details,
              originalError: error
            })

            // Clean up the failed channel
            await leaveChannelWithTimeout(channel)

            // Clear from state on failure
            set(
              (state) => {
                state.playerChannel = undefined
              },
              false,
              'joinPlayerChannel - error'
            )
          }
        },
        leavePlayerChannel: async () => {
          const { playerChannel } = useSocketStore.getState()
          await leaveChannelWithTimeout(playerChannel)

          set(
            (state) => {
              state.playerChannel = undefined
            },
            false,
            'leavePlayerChannel'
          )
        },
        joinGameChannel: async (gameId: string) => {
          const state = useSocketStore.getState()

          if (!state.socket) {
            console.error('[GAME CHANNEL] Socket not connected')
            return
          }

          // Leave and clean up previous channel
          if (state.gameChannel) {
            await leaveChannelWithTimeout(state.gameChannel)
            set(
              (state) => {
                state.gameChannel = undefined
                state.joinedGameChannelGameId = undefined
              },
              false,
              'joinGameChannel - cleanup'
            )
          }

          try {
            const channel = state.socket.channel(`realtime:public:game-${gameId}`)
            await joinChannelWithTimeout(channel, SOCKET_TIMEOUT)

            // console.log('[GAME CHANNEL] Successfully joined, channel state:', channel.state)

            set(
              (state) => {
                state.gameChannel = channel
                state.joinedGameChannelGameId = gameId
              },
              false,
              'joinGameChannel - success'
            )
          } catch (error) {
            console.error('[GAME CHANNEL] Join failed:', error)

            set(
              (state) => {
                state.gameChannel = undefined
                state.joinedGameChannelGameId = undefined
              },
              false,
              'joinGameChannel - error'
            )
          }
        },
        leaveGameChannel: async () => {
          const { gameChannel } = useSocketStore.getState()
          await leaveChannelWithTimeout(gameChannel)

          set(
            (state) => {
              state.gameChannel = undefined
              state.joinedGameChannelGameId = undefined
            },
            false,
            'leaveGameChannel'
          )
        },
        joinRaceChannel: async () => {
          const state = useSocketStore.getState()

          if (!state.socket) {
            console.error('[RACE CHANNEL] Socket not connected')
            return
          }

          // Skip if already joined
          if (state.raceChannel?.state === 'joined') {
            // console.log('[RACE CHANNEL] Already joined, skipping')
            return
          }

          // Skip if currently joining to prevent multiple simultaneous attempts
          if (state.raceChannel?.state === 'joining' || state.isJoiningRaceChannel) {
            // console.log('[RACE CHANNEL] Already joining, skipping')
            return
          }

          // Clean up any existing channel in error state
          if (
            state.raceChannel &&
            (state.raceChannel.state === 'errored' || state.raceChannel.state === 'closed')
          ) {
            // console.log('[RACE CHANNEL] Cleaning up errored channel')
            await leaveChannelWithTimeout(state.raceChannel)
            set(
              (state) => {
                state.raceChannel = undefined
              },
              false,
              'joinRaceChannel - cleanup'
            )
          }

          // Set joining flag to prevent concurrent attempts
          set(
            (state) => {
              state.isJoiningRaceChannel = true
            },
            false,
            'joinRaceChannel - starting'
          )

          // Create channel
          const channel = state.socket.channel(`realtime:public:race-api-events`)
          // console.log('[RACE CHANNEL] Creating new channel')

          try {
            await joinChannelWithTimeout(channel, SOCKET_TIMEOUT)
            // console.log('[RACE CHANNEL] Successfully joined, channel state:', channel.state)

            // Store channel in state only after successful join and clear joining flag
            set(
              (state) => {
                state.raceChannel = channel
                state.isJoiningRaceChannel = false
              },
              false,
              'joinRaceChannel - success'
            )
          } catch (error) {
            console.error('[RACE CHANNEL] Join failed:', error)

            // Clean up the failed channel
            await leaveChannelWithTimeout(channel)

            set(
              (state) => {
                state.raceChannel = undefined
                state.isJoiningRaceChannel = false
              },
              false,
              'joinRaceChannel - error'
            )
          }
        },
        leaveRaceChannel: async () => {
          const { raceChannel } = useSocketStore.getState()
          await leaveChannelWithTimeout(raceChannel)

          set(
            (state) => {
              state.raceChannel = undefined
              state.isJoiningRaceChannel = false
            },
            false,
            'leaveRaceChannel'
          )
        },
        rejoinAllChannels: () => {
          const state = useSocketStore.getState()
          const { socket, joinedGameChannelGameId } = state
          const errorState = useErrorStore.getState()

          const playerState = usePlayerStore.getState()
          const { currentPlayer } = playerState

          if (!socket) {
            console.warn('[REJOIN CHANNELS] Socket not ready')
            return
          }

          // console.log('[REJOIN CHANNELS] Attempting to rejoin all channels')

          // Don't rejoin any channels if we have a fatal error
          if (errorState.fatalError) {
            console.warn('[REJOIN CHANNELS] Fatal error exists, skipping all channel joins')
            return
          }

          // Only try to rejoin player channel if we have an authenticated player
          if (isAuthenticatedPlayer(currentPlayer) && currentPlayer.id) {
            void state.actions.joinPlayerChannel(currentPlayer.id)
          }

          if (joinedGameChannelGameId) {
            // console.log('[REJOIN CHANNELS] Rejoining game and social channels for game:', joinedGameChannelGameId)
            void state.actions.joinGameChannel(joinedGameChannelGameId)
            void state.actions.joinSocialChannel(joinedGameChannelGameId)
          }

          void state.actions.joinRaceChannel()
        }
      }
    })),
    { name: 'SocketStore', enabled: isDevtoolsEnabled() }
  )
)

export const useSocketActions = () => useSocketStore((state) => state.actions)

export const useGetSocket = () => useSocketStore((state) => state.socket)

export const useGetPlayerChannel = () => useSocketStore((state) => state.playerChannel)

export const useGetPlayerChannelJoinEpoch = () =>
  useSocketStore((state) => state.playerChannelJoinEpoch)

export const useGetGameChannel = () => useSocketStore((state) => state.gameChannel)

export const useGetJoinedGameChannelId = () =>
  useSocketStore((state) => state.joinedGameChannelGameId)

export const useGetRaceChannel = () => useSocketStore((state) => state.raceChannel)

export const useGetSocialChannel = () => useSocketStore((state) => state.socialChannel)
